import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { releasesAPI, copiesAPI, discogsAPI } from '../lib/api';
import { formatCurrency, CONDITIONS, CONDITION_OPTIONS } from '../lib/utils';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { ArrowLeft, Trash2, Save, Disc3, Info, RefreshCw, Loader2, Plus, Edit2, Copy, MapPin, Calendar, DollarSign, Layers, ChevronRight, Globe, Tag, Sun, Moon, Search, ChevronDown, Music, Clock, Crown } from 'lucide-react';
import { toast } from 'sonner';

// Currency symbols for display
const CURRENCY_SYMBOLS = {
  DKK: 'kr', SEK: 'kr', NOK: 'kr',
  EUR: '€', USD: '$', GBP: '£', CHF: 'CHF',
  CAD: 'CA$', AUD: 'A$', NZD: 'NZ$',
};

// Format sale price with currency
const formatSalePrice = (cents, currency = 'DKK') => {
  if (!cents) return '-';
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  const amount = (cents / 100).toLocaleString('da-DK');
  if (['DKK', 'SEK', 'NOK'].includes(currency)) {
    return `${amount} ${symbol}`;
  }
  return `${symbol}${amount}`;
};

// Get currency symbol
const getCurrencySymbol = (currency = 'DKK') => CURRENCY_SYMBOLS[currency] || currency;

export default function ReleaseDetail() {
  const { discogsReleaseId } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const [release, setRelease] = useState(null);
  const [copies, setCopies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingPrice, setRefreshingPrice] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [copyToDelete, setCopyToDelete] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [copyToEdit, setCopyToEdit] = useState(null);
  const [showAddCopyDialog, setShowAddCopyDialog] = useState(false);
  
  // Versions state
  const [showVersionsDialog, setShowVersionsDialog] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [versionsPage, setVersionsPage] = useState(1);
  const [versionsPagination, setVersionsPagination] = useState(null);
  const [addingVersionId, setAddingVersionId] = useState(null);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    condition_vinyl: 'VG+',
    condition_cover: 'VG+',
    purchase_price: '',
    purchase_date: '',
    notes: '',
    location: ''
  });

  // Release edit state
  const [showEditReleaseDialog, setShowEditReleaseDialog] = useState(false);
  const [releaseEditForm, setReleaseEditForm] = useState({
    year: '',
    label: '',
    country: '',
    catno: '',
    matrix: ''
  });
  
  // Find correct version state
  const [searchingVersions, setSearchingVersions] = useState(false);
  const [matchingVersions, setMatchingVersions] = useState([]);
  const [showMatchingVersions, setShowMatchingVersions] = useState(false);
  const [switchingToVersion, setSwitchingToVersion] = useState(null);
  
  // Tracklist state
  const [tracklist, setTracklist] = useState([]);
  const [tracklistOpen, setTracklistOpen] = useState(false);
  const [loadingTracklist, setLoadingTracklist] = useState(false);
  
  // For Sale state
  const [showForSaleDialog, setShowForSaleDialog] = useState(false);
  const [forSaleCopy, setForSaleCopy] = useState(null);
  const [forSalePrice, setForSalePrice] = useState('');
  const [forSaleNote, setForSaleNote] = useState('');
  const [updatingForSale, setUpdatingForSale] = useState(null);
  
  const isPro = user?.plan === 'PRO' || user?.plan === 'LIFETIME_PRO';
  
  // Get user's currency based on country
  const COUNTRY_CURRENCY_MAP = {
    DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR',
    DK: 'DKK', SE: 'SEK', NO: 'NOK', GB: 'GBP', CH: 'CHF',
    US: 'USD', CA: 'CAD', AU: 'AUD', NZ: 'NZD', JP: 'JPY',
  };
  const userCurrency = COUNTRY_CURRENCY_MAP[user?.country] || 'USD';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await releasesAPI.getRelease(parseInt(discogsReleaseId));
        setRelease(res.data.release);
        setCopies(res.data.copies);
      } catch (err) {
        toast.error('Release not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [discogsReleaseId, navigate]);

  // Fetch tracklist when collapsible opens
  const handleTracklistToggle = async (isOpen) => {
    setTracklistOpen(isOpen);
    
    if (isOpen && tracklist.length === 0 && !loadingTracklist) {
      setLoadingTracklist(true);
      try {
        const res = await discogsAPI.getRelease(parseInt(discogsReleaseId));
        const tracks = res.data.release?.tracklist || [];
        setTracklist(tracks);
      } catch (err) {
        console.error('Failed to load tracklist:', err);
        toast.error('Could not load tracklist');
      } finally {
        setLoadingTracklist(false);
      }
    }
  };

  // Format duration from seconds or "mm:ss" string
  const formatDuration = (duration) => {
    if (!duration) return null;
    // If it's already in "mm:ss" format, return as is
    if (typeof duration === 'string' && duration.includes(':')) {
      return duration;
    }
    // Convert seconds to mm:ss
    const mins = Math.floor(duration / 60);
    const secs = duration % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Fetch versions when dialog opens
  const handleShowVersions = async () => {
    if (!release?.discogs_master_id) {
      toast.error('Ingen master tilknyttet denne udgivelse');
      return;
    }
    
    setShowVersionsDialog(true);
    setLoadingVersions(true);
    setVersionsPage(1);
    
    try {
      const res = await discogsAPI.getMasterVersions(release.discogs_master_id, { page: 1, per_page: 20 });
      setVersions(res.data.versions || []);
      setVersionsPagination(res.data.pagination || null);
    } catch (err) {
      toast.error('Could not fetch versions');
    } finally {
      setLoadingVersions(false);
    }
  };

  const loadMoreVersions = async () => {
    if (!release?.discogs_master_id || loadingVersions) return;
    
    const nextPage = versionsPage + 1;
    setLoadingVersions(true);
    
    try {
      const res = await discogsAPI.getMasterVersions(release.discogs_master_id, { page: nextPage, per_page: 20 });
      setVersions([...versions, ...(res.data.versions || [])]);
      setVersionsPagination(res.data.pagination || null);
      setVersionsPage(nextPage);
    } catch (err) {
      toast.error('Could not fetch more versions');
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleSwitchVersion = (versionId) => {
    setShowVersionsDialog(false);
    navigate(`/release/${versionId}`);
  };

  // Add version directly to collection from versions dialog
  const handleAddVersionToCollection = async (version, e) => {
    e.stopPropagation(); // Prevent navigation on click
    
    setAddingVersionId(version.id);
    
    try {
      // Fetch full release data from Discogs
      const releaseRes = await discogsAPI.getRelease(version.id);
      const releaseData = releaseRes.data.release;
      
      // Prepare release payload
      const releasePayload = {
        discogs_release_id: version.id,
        discogs_master_id: release?.discogs_master_id,
        artist: releaseData?.artists?.[0]?.name || release?.artist || 'Unknown Artist',
        title: releaseData?.title || version.title || release?.title,
        year: releaseData?.year || version.year,
        genres: releaseData?.genres || release?.genres || [],
        styles: releaseData?.styles || release?.styles || [],
        cover_image_url: releaseData?.images?.[0]?.uri || version.thumb,
        label: releaseData?.labels?.[0]?.name || version.label,
        catno: releaseData?.labels?.[0]?.catno || version.catno,
        format: releaseData?.formats?.[0]?.name || version.format,
        country: releaseData?.country || version.country,
      };
      
      // Add copy
      await copiesAPI.add({
        discogs_release_id: version.id,
        condition_vinyl: 'VG+',
        condition_cover: 'VG+',
      }, releasePayload);
      
      toast.success(`"${releasePayload.title}" added to your collection!`);
      
      // Close dialog and navigate to the new release
      setShowVersionsDialog(false);
      navigate(`/release/${version.id}`);
      
    } catch (err) {
      console.error('Error adding version:', err);
      if (err.response?.status === 403) {
        toast.error(err.response?.data?.detail || 'Limit reached. Upgrade to PRO.');
      } else {
        toast.error('Could not add album');
      }
    } finally {
      setAddingVersionId(null);
    }
  };

  const refreshMarketPrice = async () => {
    setRefreshingPrice(true);
    try {
      await releasesAPI.refreshStats(parseInt(discogsReleaseId));
      const res = await releasesAPI.getRelease(parseInt(discogsReleaseId));
      setRelease(res.data.release);
      toast.success('Market price updated');
    } catch (err) {
      toast.error('Failed to refresh price');
    } finally {
      setRefreshingPrice(false);
    }
  };

  const handleEditCopy = (copy) => {
    setCopyToEdit(copy);
    setEditForm({
      condition_vinyl: copy.condition_vinyl || 'VG+',
      condition_cover: copy.condition_cover || 'VG+',
      purchase_price: copy.purchase_price || '',
      purchase_date: copy.purchase_date || '',
      notes: copy.notes || '',
      location: copy.location || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveCopy = async () => {
    try {
      await copiesAPI.update(copyToEdit.id, {
        condition_vinyl: editForm.condition_vinyl,
        condition_cover: editForm.condition_cover,
        purchase_price: editForm.purchase_price ? parseFloat(editForm.purchase_price) : null,
        purchase_date: editForm.purchase_date || null,
        notes: editForm.notes || null,
        location: editForm.location || null
      });
      
      // Refresh data
      const res = await releasesAPI.getRelease(parseInt(discogsReleaseId));
      setCopies(res.data.copies);
      setShowEditDialog(false);
      toast.success('Copy updated');
    } catch (err) {
      toast.error('Failed to update copy');
    }
  };

  const handleDeleteCopy = async () => {
    try {
      await copiesAPI.delete(copyToDelete.id);
      setCopies(copies.filter(c => c.id !== copyToDelete.id));
      setShowDeleteDialog(false);
      setCopyToDelete(null);
      toast.success('Copy removed');
      
      // Navigate back if no copies left
      if (copies.length === 1) {
        navigate('/');
      }
    } catch (err) {
      toast.error('Failed to delete copy');
    }
  };

  const handleAddCopy = async () => {
    try {
      await copiesAPI.add({
        discogs_release_id: parseInt(discogsReleaseId),
        condition_vinyl: editForm.condition_vinyl,
        condition_cover: editForm.condition_cover,
        purchase_price: editForm.purchase_price ? parseFloat(editForm.purchase_price) : null,
        purchase_date: editForm.purchase_date || null,
        notes: editForm.notes || null,
        location: editForm.location || null
      });
      
      // Refresh data
      const res = await releasesAPI.getRelease(parseInt(discogsReleaseId));
      setCopies(res.data.copies);
      setShowAddCopyDialog(false);
      toast.success('Copy added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add copy');
    }
  };

  const openAddCopyDialog = () => {
    setEditForm({
      condition_vinyl: 'VG+',
      condition_cover: 'VG+',
      purchase_price: '',
      purchase_date: '',
      notes: '',
      location: ''
    });
    setShowAddCopyDialog(true);
  };

  // For Sale handlers
  const openForSaleDialog = (copy) => {
    setForSaleCopy(copy);
    // Convert øre to DKK for display
    setForSalePrice(copy.sale_price ? (copy.sale_price / 100).toString() : '');
    setForSaleNote(copy.sale_note || '');
    setShowForSaleDialog(true);
  };

  const handleToggleForSale = async (copy) => {
    if (!isPro) {
      toast.error('Salgsliste er en PRO-funktion');
      navigate('/upgrade');
      return;
    }
    
    if (copy.for_sale) {
      // Remove from sale
      setUpdatingForSale(copy.id);
      try {
        await copiesAPI.updateForSale(copy.id, false, null, null);
        const res = await releasesAPI.getRelease(parseInt(discogsReleaseId));
        setCopies(res.data.copies);
        toast.success('Removed from sale list');
      } catch (err) {
        toast.error('Could not update');
      } finally {
        setUpdatingForSale(null);
      }
    } else {
      // Open dialog to set price
      openForSaleDialog(copy);
    }
  };

  const handleSaveForSale = async () => {
    if (!forSalePrice || parseFloat(forSalePrice) <= 0) {
      toast.error('Indtast en gyldig pris');
      return;
    }
    
    if (forSaleNote && forSaleNote.length > 140) {
      toast.error('Note must be max 140 characters');
      return;
    }
    
    setUpdatingForSale(forSaleCopy.id);
    try {
      // Convert DKK to øre
      const priceInOre = Math.round(parseFloat(forSalePrice) * 100);
      await copiesAPI.updateForSale(forSaleCopy.id, true, priceInOre, forSaleNote || null);
      const res = await releasesAPI.getRelease(parseInt(discogsReleaseId));
      setCopies(res.data.copies);
      setShowForSaleDialog(false);
      toast.success('Marked for sale!');
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Sale list is a PRO feature');
      } else {
        toast.error('Could not update');
      }
    } finally {
      setUpdatingForSale(null);
    }
  };

  const openEditReleaseDialog = () => {
    setReleaseEditForm({
      year: release?.year || '',
      label: release?.label || '',
      country: release?.country || '',
      catno: release?.catno || '',
      matrix: ''
    });
    setShowMatchingVersions(false);
    setMatchingVersions([]);
    setShowEditReleaseDialog(true);
  };

  const handleSaveRelease = async () => {
    try {
      await releasesAPI.updateRelease(parseInt(discogsReleaseId), {
        year: releaseEditForm.year ? parseInt(releaseEditForm.year) : null,
        label: releaseEditForm.label || null,
        country: releaseEditForm.country || null,
        catno: releaseEditForm.catno || null
      });
      
      // Refresh data
      const res = await releasesAPI.getRelease(parseInt(discogsReleaseId));
      setRelease(res.data.release);
      setShowEditReleaseDialog(false);
      toast.success('Album opdateret');
    } catch (err) {
      toast.error('Could not update album');
    }
  };

  const handleSearchMatchingVersions = async () => {
    // If matrix is provided, search directly via Discogs search API
    if (releaseEditForm.matrix && releaseEditForm.matrix.trim()) {
      setSearchingVersions(true);
      setShowMatchingVersions(true);
      
      try {
        // Search Discogs for releases matching the matrix/runout code
        const searchQuery = `${release?.artist || ''} ${releaseEditForm.matrix}`.trim();
        const res = await discogsAPI.search({
          q: searchQuery,
          type: 'release',
          per_page: 30
        });
        
        const results = res.data.results || [];
        
        // Transform search results to match version format
        const transformedResults = results.map(r => ({
          id: r.id,
          title: r.title,
          year: r.year,
          country: r.country,
          label: r.label?.[0],
          catno: r.catno,
          format: r.format?.join(', '),
          thumb: r.thumb || r.cover_image
        }));
        
        setMatchingVersions(transformedResults);
        
        if (transformedResults.length === 0) {
          toast.info('Ingen resultater fundet for den indtastede matrix-kode');
        } else {
          toast.success(`Fandt ${transformedResults.length} mulige matches`);
        }
      } catch (err) {
        toast.error('Could not search for pressings');
        setMatchingVersions([]);
      } finally {
        setSearchingVersions(false);
      }
      return;
    }
    
    // Fall back to master versions search if no matrix provided
    if (!release?.discogs_master_id) {
      toast.error('No master release linked - cannot search versions');
      return;
    }
    
    setSearchingVersions(true);
    setShowMatchingVersions(true);
    
    try {
      // Fetch all versions from the master
      const res = await discogsAPI.getMasterVersions(release.discogs_master_id, { page: 1, per_page: 100 });
      let allVersions = res.data.versions || [];
      
      // Filter versions based on edited form values
      const filtered = allVersions.filter(v => {
        let match = true;
        if (releaseEditForm.country && v.country) {
          match = match && v.country.toLowerCase().includes(releaseEditForm.country.toLowerCase());
        }
        if (releaseEditForm.year && v.year) {
          match = match && v.year.toString() === releaseEditForm.year.toString();
        }
        if (releaseEditForm.label && v.label) {
          match = match && v.label.toLowerCase().includes(releaseEditForm.label.toLowerCase());
        }
        if (releaseEditForm.catno && v.catno) {
          match = match && v.catno.toLowerCase().includes(releaseEditForm.catno.toLowerCase());
        }
        return match;
      });
      
      setMatchingVersions(filtered.length > 0 ? filtered : allVersions.slice(0, 20));
      
      if (filtered.length === 0) {
        toast.info('No exact match - showing all versions');
      } else {
        toast.success(`Fandt ${filtered.length} matchende udgaver`);
      }
    } catch (err) {
      toast.error('Could not search versions');
      setMatchingVersions([]);
    } finally {
      setSearchingVersions(false);
    }
  };

  const handleSwitchToVersion = async (version) => {
    setSwitchingToVersion(version.id);
    
    try {
      // Fetch full release data and market stats
      const releaseRes = await discogsAPI.getRelease(version.id);
      const releaseData = releaseRes.data.release;
      const marketStats = releaseRes.data.marketplace_stats;
      
      // Calculate estimated value (median or lowest as fallback)
      const estimatedValue = marketStats?.median?.value || marketStats?.lowest_price?.value || null;
      const currency = marketStats?.median?.currency || marketStats?.lowest_price?.currency || null;
      
      // Update the release in database with new discogs_release_id and data
      await releasesAPI.updateRelease(parseInt(discogsReleaseId), {
        year: releaseData?.year || version.year,
        label: releaseData?.labels?.[0]?.name || version.label,
        country: releaseData?.country || version.country,
        catno: releaseData?.labels?.[0]?.catno || version.catno
      });
      
      // If this is a different release ID, we need to update copies and release
      if (version.id !== parseInt(discogsReleaseId)) {
        // Create/update the new release entry
        await copiesAPI.add({
          discogs_release_id: version.id,
          condition_vinyl: copies[0]?.condition_vinyl || 'VG+',
          condition_cover: copies[0]?.condition_cover || 'VG+',
        }, {
          discogs_release_id: version.id,
          discogs_master_id: release?.discogs_master_id,
          artist: releaseData?.artists?.[0]?.name || release?.artist,
          title: releaseData?.title || release?.title,
          year: releaseData?.year || version.year,
          genres: releaseData?.genres || release?.genres || [],
          styles: releaseData?.styles || release?.styles || [],
          cover_image_url: releaseData?.images?.[0]?.uri || version.thumb,
          market_value_median: estimatedValue,
          market_value_low: marketStats?.lowest_price?.value || null,
          market_value_high: marketStats?.highest_price?.value || null,
          market_value_currency: currency,
          label: releaseData?.labels?.[0]?.name || version.label,
          catno: releaseData?.labels?.[0]?.catno || version.catno,
          format: releaseData?.formats?.[0]?.name || version.format,
          country: releaseData?.country || version.country,
        });
        
        // Delete old copies
        for (const copy of copies) {
          await copiesAPI.delete(copy.id);
        }
        
        toast.success(`Switched to ${version.country || ''} edition - market value updated!`);
        setShowEditReleaseDialog(false);
        navigate(`/release/${version.id}`);
      } else {
        // Same release, just refresh stats
        await releasesAPI.refreshStats(parseInt(discogsReleaseId));
        const res = await releasesAPI.getRelease(parseInt(discogsReleaseId));
        setRelease(res.data.release);
        setShowEditReleaseDialog(false);
        toast.success('Market Value opdateret!');
      }
    } catch (err) {
      console.error('Error switching version:', err);
      toast.error('Could not switch edition');
    } finally {
      setSwitchingToVersion(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!release) return null;

  const placeholderImage = 'https://images.unsplash.com/photo-1642444525640-d3eb287ed389?w=400&h=400&fit=crop';

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="sticky top-0 z-40 glass">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="gap-2"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleTheme}
                  className="w-9 h-9"
                  data-testid="theme-toggle-btn"
                >
                  {theme === 'dark' ? (
                    <Sun className="w-4 h-4 text-primary" />
                  ) : (
                    <Moon className="w-4 h-4 text-primary" />
                  )}
                </Button>
                <Button
                  onClick={openAddCopyDialog}
                  className="gap-2 bg-primary text-primary-foreground"
                  data-testid="add-copy-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Copy
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
          <div className="grid md:grid-cols-[400px_1fr] gap-8">
            {/* Album Cover */}
            <div className="md:sticky md:top-24 md:self-start">
              <div className="aspect-square rounded-sm overflow-hidden bg-card border border-border">
                <img
                  src={release.cover_image_url || placeholderImage}
                  alt={`${release.artist} - ${release.title}`}
                  className="w-full h-full object-cover"
                  data-testid="release-cover"
                />
              </div>
            </div>

            {/* Release Details */}
            <div className="space-y-6">
              {/* Title Section */}
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl md:text-4xl font-bold" data-testid="release-title">
                    {release.title}
                  </h1>
                  <Badge variant="secondary" className="text-lg">
                    x{copies.length}
                  </Badge>
                </div>
                <p className="text-xl text-muted-foreground" data-testid="release-artist">
                  {release.artist}
                </p>
              </div>

              {/* Metadata */}
              <div className="flex flex-wrap items-center gap-2">
                {release.year && (
                  <Badge variant="secondary">{release.year}</Badge>
                )}
                {release.label && (
                  <Badge variant="outline">{release.label}</Badge>
                )}
                {release.catno && (
                  <Badge variant="outline" className="font-mono">{release.catno}</Badge>
                )}
                {release.format && (
                  <Badge variant="outline">{release.format}</Badge>
                )}
                {release.country && (
                  <Badge variant="outline">{release.country}</Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={openEditReleaseDialog}
                  className="h-6 px-2 text-muted-foreground hover:text-foreground"
                  data-testid="edit-release-btn"
                >
                  <Edit2 className="w-3 h-3 mr-1" />
                  Edit
                </Button>
              </div>

              {/* Genres & Styles */}
              {(release.genres?.length > 0 || release.styles?.length > 0) && (
                <div className="flex flex-wrap gap-2">
                  {release.genres?.map(genre => (
                    <Badge key={genre} className="bg-primary/10 text-primary border-primary/20">
                      {genre}
                    </Badge>
                  ))}
                  {release.styles?.map(style => (
                    <Badge key={style} variant="secondary">
                      {style}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Tracklist - Collapsible */}
              <Collapsible open={tracklistOpen} onOpenChange={handleTracklistToggle}>
                <Card className="bg-card border-border">
                  <CollapsibleTrigger asChild>
                    <CardHeader className="pb-2 cursor-pointer hover:bg-secondary/30 transition-colors rounded-t-lg">
                      <CardTitle className="text-lg flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Music className="w-5 h-5 text-primary" />
                          Tracklist
                        </span>
                        <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${tracklistOpen ? 'rotate-180' : ''}`} />
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      {loadingTracklist ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        </div>
                      ) : tracklist.length > 0 ? (
                        <div className="space-y-1">
                          {tracklist.map((track, index) => {
                            const isHeading = track.type_ === 'heading' || track.type_ === 'index';
                            
                            if (isHeading) {
                              return (
                                <div key={index} className="pt-3 pb-1 first:pt-0">
                                  <p className="text-sm font-semibold text-primary">
                                    {track.title}
                                  </p>
                                </div>
                              );
                            }
                            
                            return (
                              <div 
                                key={index}
                                className="flex items-center gap-3 py-2 px-3 rounded-sm hover:bg-secondary/50 transition-colors"
                              >
                                <span className="text-sm text-muted-foreground w-6 text-right font-mono">
                                  {track.position || (index + 1)}
                                </span>
                                <span className="flex-1 text-sm">
                                  {track.title}
                                  {track.artists && track.artists.length > 0 && (
                                    <span className="text-muted-foreground">
                                      {' '}– {track.artists.map(a => a.name).join(', ')}
                                    </span>
                                  )}
                                </span>
                                {track.duration && (
                                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {formatDuration(track.duration)}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground py-4 text-center">
                          No tracklist available
                        </p>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* Market Value Card */}
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center justify-between">
                    Market Value
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={refreshMarketPrice}
                      disabled={refreshingPrice}
                      data-testid="refresh-price-btn"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshingPrice ? 'animate-spin' : ''}`} />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {release.market_value_median ? (
                    <>
                      <div className="text-3xl font-bold text-primary" data-testid="market-value">
                        {formatCurrency(release.market_value_median, release.market_value_currency)}
                        <span className="text-sm font-normal text-muted-foreground ml-2">median</span>
                      </div>
                      {(release.market_value_low || release.market_value_high) && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Range: {formatCurrency(release.market_value_low, release.market_value_currency)} - {formatCurrency(release.market_value_high, release.market_value_currency)}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground mt-2">
                        Total value ({copies.length} copies): <span className="font-semibold text-foreground">{formatCurrency(release.market_value_median * copies.length, release.market_value_currency)}</span>
                      </p>
                    </>
                  ) : (
                    <p className="text-muted-foreground">No marketplace stats available</p>
                  )}
                </CardContent>
              </Card>

              {/* Copies Section */}
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Copy className="w-5 h-5 text-primary" />
                    Your Copies ({copies.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {copies.map((copy, index) => (
                    <div
                      key={copy.id}
                      className="p-4 bg-secondary/50 rounded-sm border border-border"
                      data-testid={`copy-${copy.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-sm font-medium">Copy #{index + 1}</span>
                            <Badge variant="outline">{copy.condition_vinyl}</Badge>
                            <Badge variant="outline">{copy.condition_cover}</Badge>
                            {copy.for_sale && (
                              <Badge className="bg-green-600/20 text-green-500 border-green-600/30">
                                <Tag className="w-3 h-3 mr-1" />
                                For sale: {formatSalePrice(copy.sale_price, copy.sale_currency || userCurrency)}
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            {copy.purchase_price && (
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-3 h-3" />
                                {formatCurrency(copy.purchase_price)}
                              </span>
                            )}
                            {copy.purchase_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {copy.purchase_date}
                              </span>
                            )}
                            {copy.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {copy.location}
                              </span>
                            )}
                          </div>
                          
                          {copy.notes && (
                            <p className="text-sm text-muted-foreground italic">
                              &quot;{copy.notes}&quot;
                            </p>
                          )}
                          
                          {/* For Sale section */}
                          <div className="pt-2 flex items-center gap-3">
                            <Button
                              variant={copy.for_sale ? "secondary" : "outline"}
                              size="sm"
                              onClick={() => handleToggleForSale(copy)}
                              disabled={updatingForSale === copy.id}
                              className={`gap-2 ${copy.for_sale ? 'bg-green-600/20 hover:bg-green-600/30 text-green-500' : ''}`}
                              data-testid={`for-sale-btn-${copy.id}`}
                            >
                              {updatingForSale === copy.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Tag className="w-3 h-3" />
                              )}
                              {copy.for_sale ? 'Remove from sale' : 'Mark for sale'}
                              {!isPro && <Crown className="w-3 h-3 text-primary" />}
                            </Button>
                            {copy.for_sale && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openForSaleDialog(copy)}
                                className="gap-1 text-muted-foreground"
                                data-testid={`edit-sale-btn-${copy.id}`}
                              >
                                <Edit2 className="w-3 h-3" />
                                Edit price
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditCopy(copy)}
                            data-testid={`edit-copy-${copy.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              setCopyToDelete(copy);
                              setShowDeleteDialog(true);
                            }}
                            data-testid={`delete-copy-${copy.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Discogs IDs */}
              {(release.discogs_master_id || release.discogs_release_id) && (
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground font-mono">
                    {release.discogs_master_id && <div>Master: {release.discogs_master_id}</div>}
                    {release.discogs_release_id && <div>Release: {release.discogs_release_id}</div>}
                  </div>
                  {release.discogs_master_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (!isPro) {
                          toast.error('View all versions is a PRO feature');
                          navigate('/upgrade');
                          return;
                        }
                        handleShowVersions();
                      }}
                      className="gap-2"
                      data-testid="show-versions-btn"
                    >
                      <Layers className="w-4 h-4" />
                      View all versions
                      {!isPro && <Crown className="w-3 h-3 text-primary" />}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Versions Dialog */}
        <Dialog open={showVersionsDialog} onOpenChange={setShowVersionsDialog}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-primary" />
                All versions of &quot;{release?.title}&quot;
              </DialogTitle>
              <DialogDescription>
                Select a specific pressing or edition to add to your collection
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto space-y-2 py-4">
              {loadingVersions && versions.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : versions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No versions found
                </p>
              ) : (
                <>
                  {versions.map((version) => {
                    const isCurrentVersion = version.id === parseInt(discogsReleaseId);
                    const isAdding = addingVersionId === version.id;
                    return (
                      <div
                        key={version.id}
                        className={`p-3 rounded-sm border transition-colors ${
                          isCurrentVersion 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border bg-secondary/30 hover:bg-secondary/50'
                        }`}
                        data-testid={`version-${version.id}`}
                      >
                        <div className="flex items-start gap-3">
                          {version.thumb && (
                            <img
                              src={version.thumb}
                              alt={version.title}
                              className="w-12 h-12 rounded-sm object-cover flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm truncate">
                                {version.title}
                              </span>
                              {isCurrentVersion && (
                                <Badge variant="default" className="text-xs">
                                  Denne version
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {version.year && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {version.year}
                                </span>
                              )}
                              {version.country && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Globe className="w-3 h-3" />
                                  {version.country}
                                </span>
                              )}
                              {version.label && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Tag className="w-3 h-3" />
                                  {version.label}
                                </span>
                              )}
                            </div>
                            {version.format && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {version.format}
                              </p>
                            )}
                            {version.catno && (
                              <p className="text-xs font-mono text-muted-foreground mt-0.5">
                                {version.catno}
                              </p>
                            )}
                          </div>
                          {/* Add to collection button */}
                          {!isCurrentVersion && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-shrink-0 gap-1"
                              onClick={(e) => handleAddVersionToCollection(version, e)}
                              disabled={isAdding}
                              data-testid={`add-version-${version.id}`}
                            >
                              {isAdding ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Plus className="w-3 h-3" />
                              )}
                              <span className="hidden sm:inline">Add</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Load more button */}
                  {versionsPagination && versionsPagination.page < versionsPagination.pages && (
                    <Button
                      variant="outline"
                      onClick={loadMoreVersions}
                      disabled={loadingVersions}
                      className="w-full mt-4"
                    >
                      {loadingVersions ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : null}
                      Load more ({versionsPagination.items - versions.length} remaining)
                    </Button>
                  )}
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove this copy?</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove this copy from your collection. The release will remain if you have other copies.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteCopy}
                className="bg-destructive text-destructive-foreground"
                data-testid="confirm-delete-btn"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Copy Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Edit Copy</DialogTitle>
              <DialogDescription>Update the details for this copy</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Vinyl Condition</label>
                  <Select value={editForm.condition_vinyl} onValueChange={(v) => setEditForm({...editForm, condition_vinyl: v})}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Cover Condition</label>
                  <Select value={editForm.condition_cover} onValueChange={(v) => setEditForm({...editForm, condition_cover: v})}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Purchase Price</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editForm.purchase_price}
                    onChange={(e) => setEditForm({...editForm, purchase_price: e.target.value})}
                    className="bg-secondary border-border"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Purchase Date</label>
                  <Input
                    type="date"
                    value={editForm.purchase_date}
                    onChange={(e) => setEditForm({...editForm, purchase_date: e.target.value})}
                    className="bg-secondary border-border"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Location</label>
                <Input
                  placeholder="e.g., Shelf A, Box 3"
                  value={editForm.location}
                  onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                  className="bg-secondary border-border"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Notes</label>
                <Textarea
                  placeholder="Any notes about this copy..."
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  className="bg-secondary border-border"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveCopy} className="bg-primary text-primary-foreground">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Copy Dialog */}
        <Dialog open={showAddCopyDialog} onOpenChange={setShowAddCopyDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Add Another Copy</DialogTitle>
              <DialogDescription>Add another copy of &quot;{release?.title}&quot; to your collection</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Vinyl Condition</label>
                  <Select value={editForm.condition_vinyl} onValueChange={(v) => setEditForm({...editForm, condition_vinyl: v})}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Cover Condition</label>
                  <Select value={editForm.condition_cover} onValueChange={(v) => setEditForm({...editForm, condition_cover: v})}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Purchase Price</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editForm.purchase_price}
                    onChange={(e) => setEditForm({...editForm, purchase_price: e.target.value})}
                    className="bg-secondary border-border"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Purchase Date</label>
                  <Input
                    type="date"
                    value={editForm.purchase_date}
                    onChange={(e) => setEditForm({...editForm, purchase_date: e.target.value})}
                    className="bg-secondary border-border"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Location</label>
                <Input
                  placeholder="e.g., Shelf A, Box 3"
                  value={editForm.location}
                  onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                  className="bg-secondary border-border"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Notes</label>
                <Textarea
                  placeholder="Any notes about this copy..."
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  className="bg-secondary border-border"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddCopyDialog(false)}>Cancel</Button>
              <Button onClick={handleAddCopy} className="bg-primary text-primary-foreground">
                <Plus className="w-4 h-4 mr-2" />
                Add Copy
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Release Dialog */}
        <Dialog open={showEditReleaseDialog} onOpenChange={setShowEditReleaseDialog}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Search for pressing</DialogTitle>
              <DialogDescription>
                Indtast matrix-kode fra runout eller andre oplysninger for at finde den korrekte udgave
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 flex-1 overflow-y-auto">
              {/* Matrix/Runout field - prominent */}
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-2">
                <label className="text-sm font-medium block">Matrix / Runout kode</label>
                <Input
                  placeholder="f.eks. A-1 MASTERDISK RL eller 6359 034-A1"
                  value={releaseEditForm.matrix}
                  onChange={(e) => setReleaseEditForm({...releaseEditForm, matrix: e.target.value})}
                  className="bg-background border-border font-mono"
                  data-testid="edit-matrix-input"
                />
                <p className="text-xs text-muted-foreground">
                  The matrix code is engraved on the vinyl runout (the blank area near the label)
                </p>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-card px-2 text-muted-foreground">or search by other details</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Year</label>
                  <Input
                    type="number"
                    placeholder="f.eks. 1985"
                    value={releaseEditForm.year}
                    onChange={(e) => setReleaseEditForm({...releaseEditForm, year: e.target.value})}
                    className="bg-secondary border-border"
                    data-testid="edit-year-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Land</label>
                  <Input
                    placeholder="f.eks. Netherlands"
                    value={releaseEditForm.country}
                    onChange={(e) => setReleaseEditForm({...releaseEditForm, country: e.target.value})}
                    className="bg-secondary border-border"
                    data-testid="edit-country-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Label</label>
                  <Input
                    placeholder="f.eks. Vertigo"
                    value={releaseEditForm.label}
                    onChange={(e) => setReleaseEditForm({...releaseEditForm, label: e.target.value})}
                    className="bg-secondary border-border"
                    data-testid="edit-label-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Katalognummer</label>
                  <Input
                    placeholder="f.eks. 6359 034"
                    value={releaseEditForm.catno}
                    onChange={(e) => setReleaseEditForm({...releaseEditForm, catno: e.target.value})}
                    className="bg-secondary border-border"
                    data-testid="edit-catno-input"
                  />
                </div>
              </div>
              
              {/* Search for matching version button */}
              <div className="pt-2">
                <Button
                  variant="default"
                  onClick={() => {
                    if (!isPro) {
                      toast.error('Search by pressing is a PRO feature');
                      navigate('/upgrade');
                      return;
                    }
                    handleSearchMatchingVersions();
                  }}
                  disabled={searchingVersions}
                  className="w-full gap-2"
                  data-testid="find-version-btn"
                >
                  {searchingVersions ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  Search for pressing
                  {!isPro && <Crown className="w-3 h-3 text-primary ml-1" />}
                </Button>
              </div>
              
              {/* Matching versions list */}
              {showMatchingVersions && (
                <div className="border border-border rounded-lg p-3 space-y-2 max-h-[300px] overflow-y-auto">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Layers className="w-4 h-4 text-primary" />
                    {matchingVersions.length > 0 ? `Matchende udgaver (${matchingVersions.length})` : 'Ingen matchende udgaver'}
                  </h4>
                  
                  {matchingVersions.map((version) => {
                    const isCurrentVersion = version.id === parseInt(discogsReleaseId);
                    const isSwitching = switchingToVersion === version.id;
                    
                    return (
                      <div
                        key={version.id}
                        className={`p-3 rounded-md border transition-colors ${
                          isCurrentVersion 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border bg-secondary/30 hover:bg-secondary/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {version.thumb && (
                            <img
                              src={version.thumb}
                              alt=""
                              className="w-10 h-10 rounded object-cover flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {version.year && (
                                <Badge variant="secondary" className="text-xs">{version.year}</Badge>
                              )}
                              {version.country && (
                                <Badge variant="outline" className="text-xs">{version.country}</Badge>
                              )}
                              {version.label && (
                                <span className="text-xs text-muted-foreground">{version.label}</span>
                              )}
                              {isCurrentVersion && (
                                <Badge variant="default" className="text-xs">Current</Badge>
                              )}
                            </div>
                            {version.catno && (
                              <p className="text-xs font-mono text-muted-foreground mt-1">{version.catno}</p>
                            )}
                            {version.format && (
                              <p className="text-xs text-muted-foreground">{version.format}</p>
                            )}
                          </div>
                          {!isCurrentVersion && (
                            <Button
                              size="sm"
                              onClick={() => handleSwitchToVersion(version)}
                              disabled={isSwitching}
                              className="flex-shrink-0 gap-1"
                            >
                              {isSwitching ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <RefreshCw className="w-3 h-3" />
                              )}
                              Brug denne
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              <p className="text-xs text-muted-foreground">
                Tip: Save changes only updates text. Use &quot;Find correct version&quot; to update market value.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-border">
              <Button variant="outline" onClick={() => setShowEditReleaseDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveRelease} className="bg-primary text-primary-foreground">
                <Save className="w-4 h-4 mr-2" />
                Save changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* For Sale Dialog */}
        <Dialog open={showForSaleDialog} onOpenChange={setShowForSaleDialog}>
          <DialogContent className="bg-card border-border sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-green-500" />
                Mark for sale
              </DialogTitle>
              <DialogDescription>
                Set your sale price in {userCurrency} and an optional note
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="sale-price" className="text-sm font-medium">Price ({userCurrency})</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="sale-price"
                    type="number"
                    step="1"
                    min="1"
                    placeholder="250"
                    value={forSalePrice}
                    onChange={(e) => setForSalePrice(e.target.value)}
                    className="bg-secondary border-border pr-12"
                    data-testid="for-sale-price-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    {getCurrencySymbol(userCurrency)}
                  </span>
                </div>
              </div>
              
              <div>
                <Label htmlFor="sale-note" className="text-sm font-medium">Note (optional)</Label>
                <Textarea
                  id="sale-note"
                  placeholder="e.g. First pressing, small crack on cover..."
                  value={forSaleNote}
                  onChange={(e) => setForSaleNote(e.target.value)}
                  maxLength={140}
                  className="bg-secondary border-border mt-1.5 resize-none"
                  rows={2}
                  data-testid="for-sale-note-input"
                />
                <p className="text-xs text-muted-foreground mt-1 text-right">
                  {forSaleNote.length}/140
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForSaleDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveForSale} 
                disabled={updatingForSale === forSaleCopy?.id}
                className="bg-green-600 hover:bg-green-700 text-white gap-2"
                data-testid="save-for-sale-btn"
              >
                {updatingForSale === forSaleCopy?.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Tag className="w-4 h-4" />
                )}
                Mark for sale
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
